import { Typography } from "../typography";
import { CreatorAvatar } from "../creator/creator-avatar";
import { cn } from "@drpcrd/utils/shared";
import { LogoAvatar } from "../logo-avatar";
import { formatInboxTimestamp } from "@drpcrd/utils/formatters";

export type NotificationCardProps = {
  title: string;
  subtitle: string;
  isUnread?: boolean;
  campaignAvatar?: string;
  creatorSrc?: string;
  social1Src?: string;
  social2Src?: string;
  variant?: "creator" | "brand";
  /** Formatted and displayed on the right side of the row (creator variant only). */
  timestamp?: Date;
};

/**
 * Small pink dot shown in the corner when the notification is unread.
 */
const UnreadDot = () => (
  <div className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full border-2 border-white bg-pink-500" />
);

/**
 * Chooses which avatar to render based on the app context:
 * - In the **brand app** (variant = "brand") → show the **CreatorAvatar**
 *   so brands can see which creator the notification is about.
 * - In the **creator app** (variant = "creator") → show the **LogoAvatar**
 *   so creators can see which brand the notification is from.
 *
 * This keeps the perspective consistent:
 * each side always sees the "other party" in the notification.
 */
const NotificationAvatar = ({
  variant,
  campaignAvatar,
  creatorSrc,
}: Pick<NotificationCardProps, "variant" | "campaignAvatar" | "creatorSrc">) =>
  variant === "creator" ? (
    <LogoAvatar logoUrl={campaignAvatar} className="h-3 w-3" />
  ) : (
    <CreatorAvatar creatorSrc={creatorSrc} size="xs" />
  );

/**
 * Notification card component used inside the feed/sidebar.
 * - Supports two variants: "creator" and "brand"
 * - Shows avatar, title, subtitle, and unread dot
 *
 * **Creator variant** renders as a flat list row: no shadow, no rounded corners,
 * no horizontal padding, `border-b` divider, and an optional formatted timestamp on the right.
 *
 * **Brand variant** renders as a card with padding and a hover border.
 */
export const NotificationCard = ({
  title,
  subtitle,
  isUnread = false,
  campaignAvatar,
  creatorSrc,
  variant = "brand",
  timestamp,
}: NotificationCardProps) => (
  <div
    className={cn(
      "relative flex cursor-pointer items-start gap-1.5",
      variant === "creator"
        ? "border-border-primary border-b bg-white py-2"
        : "rounded-lg border border-transparent p-1 hover:bg-neutral-200",
    )}
  >
    <div className="relative mt-0.5 flex-shrink-0">
      <NotificationAvatar
        variant={variant}
        campaignAvatar={campaignAvatar}
        creatorSrc={creatorSrc}
      />
      {isUnread && <UnreadDot />}
    </div>

    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <Typography size="sm" weight="semibold" className="line-clamp-1">
          {title}
        </Typography>
        {variant === "creator" && timestamp && (
          <Typography
            size="sm"
            color="neutral"
            className="shrink-0 leading-2 whitespace-nowrap"
          >
            {formatInboxTimestamp(timestamp)}
          </Typography>
        )}
      </div>
      <Typography
        size={variant === "creator" ? "sm" : "xs"}
        color="neutral"
        className="line-clamp-1 normal-case"
      >
        {subtitle}
      </Typography>
    </div>
  </div>
);
